import { onMounted, ref, computed } from 'vue';

/**
 * Evento propietario de Chromium: no existe en la libreria estandar de tipos del DOM.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Singleton de modulo: 'beforeinstallprompt' se dispara UNA sola vez y muy temprano en la carga,
// normalmente antes de que monte el componente del menu. Escuchar aqui (al importar el modulo)
// evita perderlo; hacerlo dentro de onMounted dejaba el evento sin capturar y el boton no abria
// el dialogo nativo de Android.
let deferredEvent: BeforeInstallPromptEvent | null = null;
const hasNativePrompt = ref(false);
const isInstalled = ref(false);

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Evita el mini-infobar de Chrome para lanzar el dialogo desde nuestro boton.
    event.preventDefault();
    deferredEvent = event as BeforeInstallPromptEvent;
    hasNativePrompt.value = true;
  });
  window.addEventListener('appinstalled', () => {
    deferredEvent = null;
    hasNativePrompt.value = false;
    isInstalled.value = true;
  });
}

function detectIos(): boolean {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ se identifica como Mac; el puntero tactil lo delata.
  return /iPad|iPhone|iPod/i.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'ios' | 'unavailable';

/**
 * Permite anclar la app a la pantalla de inicio / escritorio.
 *
 * Chromium (Android, Windows, macOS, Linux) expone 'beforeinstallprompt' y abre el dialogo
 * nativo; iOS no lo implementa, asi que ahi solo se pueden mostrar las instrucciones manuales
 * de Safari. En navegadores sin soporte (Firefox desktop, Safari macOS) install() devuelve
 * 'unavailable' y el llamador informa al usuario -- no hay API para ofrecerlo ahi.
 */
export function usePwaInstall() {
  const isIos = ref(false);

  /**
   * Se ofrece en cualquier plataforma mientras la app no este ya instalada. No se exige el
   * evento nativo: en iOS nunca llega, y en Chromium puede tardar en emitirse (pide un minimo
   * de interaccion), asi que el boton hace de respaldo manual.
   */
  const canInstall = computed(() => !isInstalled.value);

  const detectInstalled = () => {
    const standaloneDisplay = window.matchMedia('(display-mode: standalone)').matches;
    // Safari en iOS no soporta display-mode y usa esta bandera propietaria.
    const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    isInstalled.value = standaloneDisplay || iosStandalone;
  };

  /**
   * En Android va directo al dialogo nativo; 'ios' y 'unavailable' los resuelve el llamador,
   * que es quien sabe como avisar al usuario.
   */
  const install = async (): Promise<InstallOutcome> => {
    if (isIos.value) return 'ios';
    if (!deferredEvent) return 'unavailable';

    await deferredEvent.prompt();
    const { outcome } = await deferredEvent.userChoice;
    // El evento se consume tras usarlo: el navegador emitira uno nuevo si sigue siendo instalable.
    deferredEvent = null;
    hasNativePrompt.value = false;
    if (outcome === 'accepted') {
      isInstalled.value = true;
    }
    return outcome;
  };

  onMounted(() => {
    isIos.value = detectIos();
    detectInstalled();
  });

  return {
    canInstall,
    hasNativePrompt,
    isIos,
    install,
  };
}
